import React, { useMemo } from 'react';
import { Button, Card, CardHeader, Icon, Loading, Paragraph, Skeleton } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import type { GetProgramHomeResult } from '@aidkitorg/aidkit/lib/admin_panel/programHome';
import { NavLink } from 'react-router-dom';

interface WhatsNewProps {
  programHomeData: GetProgramHomeResult | null;
  isProgramHomeLoading: boolean;
  title?: string;
}

export const WhatsNew: React.FC<WhatsNewProps> = ({ programHomeData, isProgramHomeLoading, title }) => {
  const L = useLocalizedStrings();
  const displayTitle = title || L.admin_panel.program_home.whats_new.title;

  // Memoized content sections
  const applicationsContent = useMemo(() => {
    if (!programHomeData?.applicationAndReviews) return null;

    const { submitted, inReview, rejected } = programHomeData.applicationAndReviews;
    const parts = [];

    if (submitted) parts.push(`${submitted} application${submitted !== 1 ? 's' : ''} submitted`);
    if (inReview) parts.push(`${inReview} in review`);
    if (rejected) parts.push(`${rejected} rejected`);

    if (parts.length === 0) return null;

    const formattedText = parts.length === 1
      ? parts[0]
      : parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];

    return (
      <>
        <b>Applications & Reviews</b> {formattedText} in the last 24 hours.
        <br/>
      </>
    );
  }, [programHomeData?.applicationAndReviews]);

  const fraudContent = useMemo(() => {
    if (!programHomeData?.fraud?.markedFraud || programHomeData.fraud.markedFraud === 0) return null;

    return (
      <>
        <b>Fraud Alerts</b> {programHomeData.fraud.markedFraud} customer{programHomeData.fraud.markedFraud !== 1 ? 's' : ''} currently flagged for fraud review. Learn more in{' '}
        <NavLink
          to={`/admin-panel/applicants`}
        >
          Applicants.
        </NavLink>
      </>
    );
  }, [programHomeData?.fraud?.markedFraud]);


  // Don't render if no configuration is set up
  if (!programHomeData || programHomeData.hasConfiguration === false) return <></>

  const hasContent = applicationsContent || fraudContent;

  return (
    <Card className="p-1 w-full rounded-md">
      <CardHeader
        title={
          <div className="flex items-center gap-x-2">
            <Icon className="text-[var(--ak-identity-color-blue)]" name='NewspaperIcon' type="solid" />
            <div className="font-bold text-xl">{displayTitle}</div>
          </div>
        }
      />
      {isProgramHomeLoading ? (
        <div className="py-2">
          <Skeleton variant="sentences" size="sm"/>
        </div>
      ) : hasContent ? (
        <Paragraph>
          {applicationsContent}
          {fraudContent}
        </Paragraph>
      ) : (
        <Paragraph>
          No new updates for today.
        </Paragraph>
      )}
    </Card>
  );
};
